import React, {useCallback, useEffect, useMemo, useState} from 'react';
import ZoomableImage from './ZoomableImage';
import {getHighlightedLocationSuffixes} from '../../utils/locationParser';
import {isPlaceNavigable} from '../../config/featureFlags';
import './ZoomableSvg.scss';

interface ZoomableSvgProps {
  svgUrl: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onLocationClick?: (locationCode: string) => void;
  highlightedLocationCode?: string;
  className?: string;
  placeId?: string;
}

const LOCATION_ID_PREFIX = 'location-';

// TEMPORARY: Pattern to match circle elements with location IDs - used to remove circles for non-navigable places
const LOCATION_CIRCLE_PATTERN = /<circle[^>]*id="location-[^"]*"[^>]*\/?>/gi;

const ZoomableSvg: React.FC<ZoomableSvgProps> = ({
                                                   svgUrl,
                                                   isExpanded,
                                                   onToggleExpand,
                                                   onLocationClick,
                                                   highlightedLocationCode,
                                                   className,
                                                   placeId
                                                 }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [svgLoaded, setSvgLoaded] = useState(false);
  const navigable = isPlaceNavigable(placeId);

  useEffect(() => {
    const fetchSvg = async () => {
      setSvgLoaded(false);
      try {
        const response = await fetch(svgUrl);
        const text = await response.text();
        setSvgContent(text);
        setSvgLoaded(true);
      } catch (error) {
        console.error(`Failed to fetch SVG: ${svgUrl}`, error);
      }
    };

    fetchSvg();
  }, [svgUrl]);

  const processedSvgContent = useMemo(() => {
    if (!svgContent) return svgContent;

    let result = svgContent;

    // TEMPORARY: Remove location circles for non-navigable places
    // This is a workaround until all plans are properly configured for navigation
    if (!navigable) {
      return result.replace(LOCATION_CIRCLE_PATTERN, '');
    }

    if (!highlightedLocationCode) return result;

    // Get all location suffixes that should be highlighted (with ranges expanded)
    const suffixes = getHighlightedLocationSuffixes(highlightedLocationCode);
    if (suffixes.length === 0) return result;

    for (const suffix of suffixes) {
      const targetId = `${LOCATION_ID_PREFIX}${suffix}`;
      const idPattern = new RegExp(`(id="${targetId}")`, 'i');

      if (idPattern.test(result)) {
        result = result.replace(
          new RegExp(`(<[^>]*id="${targetId}"[^/>]*)(class="([^"]*)")?`, 'i'),
          (match, before, classAttr, existingClasses) => {
            if (classAttr) {
              return `${before}class="${existingClasses} location-highlighted"`;
            } else {
              return `${before} class="location-highlighted"`;
            }
          }
        );
      }
    }
    return result;
  }, [svgContent, highlightedLocationCode, navigable]);

  const handleSvgClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!onLocationClick || !navigable) return;

    const target = event.target as Element;
    const clickedElement = target.closest(`[id^="${LOCATION_ID_PREFIX}"]`);

    if (clickedElement) {
      const locationId = clickedElement.id;
      const locationCode = locationId.replace(LOCATION_ID_PREFIX, '');
      onLocationClick(locationCode);
    }
  }, [onLocationClick, navigable]);

  if (!svgContent) {
    return null;
  }

  return (
    <ZoomableImage
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      contentLoaded={svgLoaded}
      resetKey={svgUrl}
    >
      <div
        className={`zoomable-svg-container ${className || ''}`}
        onClick={handleSvgClick}
        dangerouslySetInnerHTML={{__html: processedSvgContent || ''}}
      />
    </ZoomableImage>
  );
};

export default ZoomableSvg;

